import { Button, type ButtonOption, Icon, Typo } from '@/ui/component/atom';
import { bodyFix, bodyUnfix, getSharedData } from '@/ui/util';
import { each, entries, filter, map, pipe, reduce } from '@fxts/core';
import { $, CustomEventWithDetail, CustomEventWithoutDetail, html, rune, View } from 'rune-ts';
import klass from './Modal.module.scss';

export class ConfirmToggleActiveEvent extends CustomEventWithDetail<{ active: boolean }> {}
export class ModalFooterRedrawEvent extends CustomEventWithoutDetail {}

export class ModalBodyView<T extends object = object> extends View<T> {
  open() {}
  confirm(): Promise<boolean> | boolean {
    return true;
  }
  cancel(): Promise<boolean> | boolean {
    return true;
  }
  close(): Promise<boolean> | boolean {
    return true;
  }
  getModal(): Modal {
    return this.parentView as Modal;
  }
}

export interface ModalData {
  view: ModalBodyView;
}

export interface ModalOption {
  header: {
    title: string;
  };
  footer: {
    confirm: string;
    confirm_option?: ButtonOption;
    cancel: string;
    cancel_option?: ButtonOption;
    margin?: {
      top?: number;
      bottom?: number;
    };
    type?: 'default' | 'confirm_only';
  };
  backdrop?: boolean;
  append_to?: string | Element;
}

class ModalFooterView extends View<{ modal: Modal }> {
  override template() {
    return html`
      <div class="${klass.footer}" ${this.makeMarginStyle()}>
        ${this.data.modal.option.footer.type === 'confirm_only' ? '' : this.data.modal.button.cancel}
        ${this.data.modal.button.confirm}
      </div>
    `;
  }

  private makeMarginStyle() {
    const { margin } = this.data.modal.option.footer;

    if (!margin) {
      return html``;
    }

    const style = pipe(
      margin,
      entries,
      map(([key, value]) => html`margin-${key}: ${value}px`),
      reduce((a, b) => html`${a}; ${b}`),
    );

    return html`style="${style}"`;
  }

  override redraw() {
    super.redraw();
    this.dispatchEvent(ModalFooterRedrawEvent, { bubbles: true });
    return this;
  }
}

/**
 * @description 부모 요소에 transform이 선언되어 있다면 해당 요소를 기준으로 모달이 표현됩니다.
 * */
export class Modal extends View<ModalData> {
  transitioning = false;
  footer: ModalFooterView;
  button: {
    close: Icon;
    cancel: Button;
    confirm: Button;
  };

  constructor(
    data: ModalData,
    public option: ModalOption,
  ) {
    super(data);

    const { theme } = getSharedData(this);
    const { footer } = this.option;
    const { confirm_option, cancel_option } = footer;

    const cancel_color = cancel_option?.color ?? 'line';
    const confirm_color = confirm_option?.color ?? 'primary';
    const confirm_disabled = confirm_option?.disabled ?? false;

    this.button = {
      close: new Icon({ name: 'close', size: theme === 'MarppleShop' ? 20 : 24 }),
      cancel: new Button(
        {
          label: new Typo({ text: footer.cancel }, { typo: theme === 'Marpple' ? 'p2' : 'p1', weight: 'bold' }),
        },
        {
          size: theme === 'Marpple' ? 'small' : 'large',
          type: 'button',
          color: cancel_color,
        },
      ),
      confirm: new Button(
        {
          label: new Typo({ text: footer.confirm }, { typo: theme === 'Marpple' ? 'p2' : 'p1', weight: 'bold' }),
        },
        {
          size: theme === 'Marpple' ? 'small' : 'large',
          type: 'button',
          color: confirm_color,
          disabled: confirm_disabled,
        },
      ),
    };

    this.footer = new ModalFooterView({ modal: this });
  }

  override template() {
    const { theme, is_app } = getSharedData(this);
    const { header, backdrop } = this.option;
    return html`
      <div class="${klass.modal_container}">
        ${backdrop ? html`<div class="${klass.backdrop}"></div>` : ''}
        <div class="${klass.modal}">
          <div class="${klass.header}">
            <span class="${klass.title}">${new Typo({ text: header.title }, { typo: 'h3', weight: 'bold' })}</span>
            ${is_app && theme === 'Ciety' ? '' : html`<button type="button">${this.button.close}</button>`}
          </div>
          <div class="${klass.body_container}">
            <div class="${klass.body_wrapper}"><div class="${klass.body}">${this.data.view}</div></div>
          </div>
          ${this.footer}
        </div>
      </div>
    `;
  }

  private addClickHandler(detaches: (() => void)[], res: (value: 'close' | 'cancel' | 'confirm') => void) {
    pipe(
      this.button,
      entries,
      filter(([_, view]) => view.isRendered()),
      map(([key, view]): [() => void, View] => {
        const handler = async () => {
          const closed = await this.close(key);
          if (closed) {
            detaches.forEach((detach) => detach());
            res(key);
          }
        };

        return [handler, view];
      }),
      each(([handler, view]) => {
        const detach = () => {
          view.removeEventListener('click', handler);
        };
        detaches.push(detach);
        view.addEventListener('click', handler);
      }),
    );
  }

  private clickHandler<RES extends (value: 'close' | 'cancel' | 'confirm') => void>(res: RES) {
    let detaches: (() => void)[] = [];
    this.addClickHandler(detaches, res);
    this.addEventListener(ModalFooterRedrawEvent, () => {
      detaches.forEach((detach) => {
        detach();
        return false;
      });
      detaches = [];
      this.addClickHandler(detaches, res);
    });
  }

  open() {
    if (this.option.append_to) {
      let target;
      if (typeof this.option.append_to === 'string') {
        target = document.body.querySelector(this.option.append_to);
      } else {
        target = this.option.append_to;
      }
      if (target) target.appendChild(this.render());
    } else {
      document.body.appendChild(this.render());
    }

    const element = this.element();
    const modal = element.querySelector(`.${klass.modal}`);
    const backdrop = element.querySelector(`.${klass.backdrop}`);
    element.classList.add(klass.open);
    backdrop?.classList.add(klass.open);
    modal?.classList.add(klass.animate, klass.slide_in);
    bodyFix();
    this.data.view.open();

    return new Promise<'close' | 'cancel' | 'confirm'>(this.clickHandler.bind(this));
  }

  async close(event: 'close' | 'cancel' | 'confirm') {
    const element = this.element();
    const modal = element.querySelector(`.${klass.modal}`);
    const backdrop = element.querySelector(`.${klass.backdrop}`);

    const need_close = await this.data.view[event]();

    if (!need_close) return false;

    return await new Promise<true>((res) => {
      const onEndAnimation = () => {
        modal?.classList.remove(klass.animate, klass.slide_out);
        element.classList.remove(klass.open);
        modal?.removeEventListener('animationend', onEndAnimation);
        bodyUnfix();
        this.element().remove();
        res(true);
      };

      modal?.addEventListener('animationend', onEndAnimation);
      modal?.classList.add(klass.animate, klass.slide_out);
      backdrop?.classList.remove(klass.open);
    });
  }

  override onRender() {
    this.addEventListener(ConfirmToggleActiveEvent, (e) => {
      const { active } = e.detail;
      this.button.confirm.option.disabled = !active;
      this.button.confirm.option.color = active ? (this.option.footer.confirm_option?.color ?? 'primary') : 'disabled';
      this.button.confirm.redraw();
    });
  }

  transition(view: ModalBodyView) {
    if (this.transitioning) return;

    this.transitioning = true;
    this.data.view = view;
    this.data.view.parentView = this;

    return new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        const new_body$ = $.fromHtml(`<div class="${klass.body} ${klass.fade_in}"></div>`).append(
          this.data.view.render(),
        );

        $(this.element()).find(`.${klass.body}`)?.addClass(klass.fade_out)?.after(new_body$);

        const body_wrapper$ = $(this.element()).find(`.${klass.body_wrapper}`)?.element();
        const old_body = $(this.element()).find(`.${klass.body}.${klass.fade_out}`);
        const new_body = $(this.element()).find(`.${klass.body}.${klass.fade_in}`);

        if (body_wrapper$) {
          body_wrapper$.style.height = (old_body?.element().scrollHeight ?? 0) + 'px';
          body_wrapper$.style.height = (new_body?.element().scrollHeight ?? 0) + 'px';
        }

        new_body$.element().addEventListener('animationend', () => {
          old_body?.element().remove();
          new_body?.removeClass(klass.fade_in);
          if (body_wrapper$) {
            body_wrapper$.style.height = '';
          }
          this.transitioning = false;
          resolve();
        });
      });
    });
  }

  static async open(data: ModalData, option: ModalOption) {
    const modal = new Modal(data, option);
    return modal.open();
  }

  static find() {
    const modal$ = document.querySelector(`.${Modal}`);
    if (!modal$) return;

    const modal = rune.getView(modal$, Modal);
    if (!modal) return;

    return modal;
  }
}
